import { FC } from "react";
import { useFormContext, useFieldArray } from "react-hook-form";
import { CardContainer } from "@/components/commons/CardContainer";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { useDialog } from "@/hooks/useDialog";
import { IconAddCircle } from "@/assets/icons";
import { TExamCreateForm } from "@/features/learning-content/types/learningContent";
import { Grid, Stack, Typography } from "@mui/material";
import { IconClose } from "@/assets/icons/IconClose";
import { button, text } from "@/theme/colors";

export const RelatedLinksForm: FC = () => {
  const { control, getValues } = useFormContext<TExamCreateForm>();
  const { fields, append, remove } = useFieldArray({ control, name: "relatedLinks" });
  const canAddMore = fields.length < 10;
  const confirmRemove = useDialog();

  const hasData = (index: number): boolean => {
    const link = getValues(`relatedLinks.${index}`);
    return !!(link?.label || link?.url);
  };

  const handleRemove = async (index: number) => {
    if (hasData(index)) {
      const confirmed = await confirmRemove.show();
      if (!confirmed) return;
    }
    remove(index);
  };

  return (
    <CardContainer sx={{ borderRadius: "8px" }}>
      <Stack gap={3}>
        <Typography variant="sub16Bold" sx={{ fontSize: "20px", fontWeight: 700 }}>
          関連リンク
        </Typography>

        {fields.map((field, index) => {
          const isLast = index === fields.length - 1;

          return (
            <Stack gap={1} key={field.id}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormField control={control} name={`relatedLinks.${index}.label`} label={`リンク名 ${index + 1}`}>
                    <Input fullWidth placeholder="入力してください" />
                  </FormField>
                </Grid>
                <Grid item xs={6}>
                  <FormField control={control} name={`relatedLinks.${index}.url`} label={`URL ${index + 1}`}>
                    <Input fullWidth placeholder="入力してください" />
                  </FormField>
                </Grid>
              </Grid>

              <Stack direction="row" alignItems="center" gap={2}>
                {/* Show add button only on the last row, disabled at max 10 */}
                {isLast && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    gap={0.5}
                    onClick={canAddMore ? () => append({ label: "", url: "" }) : undefined}
                    sx={{
                      cursor: canAddMore ? "pointer" : "default",
                      color: text.primary,
                      opacity: canAddMore ? 1 : 0.4,
                      pointerEvents: canAddMore ? "auto" : "none",
                      width: "fit-content",
                    }}
                  >
                    <IconAddCircle fontSize={20} color={button.primary} />
                    <Typography variant="cap12">リンクを追加</Typography>
                  </Stack>
                )}

                {fields.length > 1 && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    gap={0.5}
                    onClick={() => handleRemove(index)}
                    sx={{ cursor: "pointer", color: text.primary, width: "fit-content" }}
                  >
                    <IconClose fontSize={20} />
                    <Typography variant="cap12">削除</Typography>
                  </Stack>
                )}
              </Stack>
            </Stack>
          );
        })}

        {/* Show add button when no fields exist */}
        {fields.length === 0 && (
          <Stack
            direction="row"
            alignItems="center"
            gap={0.5}
            onClick={() => append({ label: "", url: "" })}
            sx={{ cursor: "pointer", color: text.primary, width: "fit-content" }}
          >
            <IconAddCircle fontSize={20} color={button.primary} />
            <Typography variant="cap12">リンクを追加</Typography>
          </Stack>
        )}
      </Stack>

      <ConfirmSaveModal title="削除の確認" open={confirmRemove.open} onOk={confirmRemove.confirm} onClose={confirmRemove.cancel}>
        <Typography component="p" variant="subtitle1">
          「リンク」を削除してもよろしいですか？
        </Typography>
      </ConfirmSaveModal>
    </CardContainer>
  );
};
